import {
  createCheckout as ecomCreateCheckout,
  createOrder as ecomCreateOrder,
} from '@wix/ambassador-ecom-v1-checkout/http';
import {
  ApiAddress,
  ChannelType,
  CreateCheckoutRequest,
  CreateCheckoutResponse,
} from '@wix/ambassador-ecom-v1-checkout/types';
import { Membership } from '@wix/ambassador-totals-calculator/http';
import type { Experiments, IHttpClient } from '@wix/yoshi-flow-editor';
import { ExperimentsConsts } from '../../../consts/experiments';
import { ContactDetails } from '../../../types/ambassador/bookings/ambassador-bookings-v2-booking';
import { getServiceSlotIdentifier, mapToArray } from '../../../utils';
import { mapCheckoutBookingError } from '../../../utils/errors/errors';
import { BOOKINGS_APP_DEF_ID } from '../../../utils/flow-api-adapter/consts';
import {
  ServiceData,
  SlotService,
  SlotServices,
} from '../../../utils/state/types';
import { OnError } from '../../types';
import { FormApiContext, withErrorBoundary } from '../utils';

export type GeneralBookingCheckoutFlowArgs = {
  serviceData: ServiceData;
  bookingIds: { [key: string]: string };
  bookingId?: string | null;
  contactDetails: ContactDetails;
  selectedMembership?: Membership;
  onError?: OnError;
  isCart?: boolean;
  country: string;
  couponCheckboxChecked?: boolean;
};

export async function generalBookingCheckoutFlow({
  reportError,
  experiments,
  serviceData,
  bookingIds,
  contactDetails,
  httpClient,
  onError,
  isCart,
  country,
  couponCheckboxChecked,
}: GeneralBookingCheckoutFlowArgs & FormApiContext) {
  if (Object.keys(bookingIds).length) {
    const { data: createCheckoutResponse, error: createCheckoutError } =
      await withErrorBoundary(
        {
          fn: () =>
            createCheckout(
              serviceData,
              bookingIds,
              contactDetails,
              country,
              experiments,
              httpClient,
            ),
          mapError: (e) => {
            return {
              error: mapCheckoutBookingError(e),
              shouldReport: true,
            };
          },
          fallback: {},
        },
        reportError,
      );

    if (createCheckoutError) {
      onError?.(createCheckoutError);
    }

    if (
      shouldNotGoThroughCheckout({
        createCheckoutResponse,
        isCart,
        couponCheckboxChecked,
        slotServices: serviceData.slotServices,
      })
    ) {
      const {
        data: createOrderResponse,
        error: createOrderError,
        message: errorMessage,
      } = await withErrorBoundary(
        {
          fn: () => createOrder(createCheckoutResponse, httpClient),
          mapError: (e) => ({
            error: mapCheckoutBookingError(e),
            shouldReport: true,
          }),
          fallback: {},
        },
        reportError,
      );

      if (createOrderError) {
        onError?.(createOrderError, errorMessage);
      }

      return {
        createCheckoutResponse: createOrderResponse,
        bookingIds,
      };
    } else {
      return {
        createCheckoutResponse,
        bookingIds,
      };
    }
  }
  return {
    bookingIds,
    createCheckoutResponse: {},
  };
}

async function createCheckout(
  serviceData: ServiceData,
  bookingIds: { [key: string]: string },
  contactDetails: ContactDetails,
  country: string,
  experiments: Experiments,
  httpClient: IHttpClient,
) {
  const isMultiLocationUoUEnabled = experiments.enabled(
    ExperimentsConsts.MultiLocationUoU,
  );
  const isSkipCustomerDetailsStepEnabled = experiments.enabled(
    ExperimentsConsts.SkipCustomerDetailsStep,
  );
  const businessLocationId =
    mapToArray<SlotService>(serviceData.slotServices)[0].nestedSlot.location
      ?.id ?? undefined;
  const createCheckoutRequest: CreateCheckoutRequest = {
    channelType: ChannelType.WEB,
    lineItems: [],
    checkoutInfo: {
      ...(isMultiLocationUoUEnabled && businessLocationId
        ? { businessLocationId }
        : {}),
      billingInfo: {
        contactDetails,
        ...(contactDetails.fullAddress && country
          ? {
              address: {
                ...(contactDetails.fullAddress as ApiAddress),
                country,
              },
            }
          : {}),
      },
      shippingInfo: isSkipCustomerDetailsStepEnabled
        ? {
            shippingDestination: {
              contactDetails,
            },
          }
        : undefined,
      buyerInfo: {
        email: contactDetails.email!,
        ...(contactDetails.contactId
          ? { contactId: contactDetails.contactId as string }
          : {}),
      },
      membershipOptions: {
        selectedMemberships: {
          memberships: [],
        },
      },
    },
  };

  mapToArray<SlotService>(serviceData.slotServices).forEach((slotService) => {
    createCheckoutRequest.lineItems?.push({
      quantity: 1,
      id: slotService.nestedSlot.lineItemId,
      catalogReference: {
        catalogItemId:
          bookingIds[getServiceSlotIdentifier(slotService.nestedSlot)],
        appId: BOOKINGS_APP_DEF_ID,
      },
    });
    const selectedMembership =
      slotService.memberships?.eligibleMemberships?.find(
        (membership) => membership?.id === slotService.selectedPaymentOption.id,
      );

    if (selectedMembership) {
      createCheckoutRequest.checkoutInfo?.membershipOptions?.selectedMemberships?.memberships?.push(
        {
          id: selectedMembership.id,
          appId: selectedMembership.appId,
          lineItemIds: [slotService.nestedSlot.lineItemId],
        },
      );
    }
  });

  return (await httpClient.request(ecomCreateCheckout(createCheckoutRequest)))
    .data;
}

function isFreeOrPricePlanCheckoutFlow(
  createCheckoutResponse: CreateCheckoutResponse,
) {
  const payNowAmount = createCheckoutResponse?.checkout?.payNow?.total?.amount;
  const payLaterAmount =
    createCheckoutResponse?.checkout?.payLater?.total?.amount;
  return (
    payNowAmount &&
    Number(payNowAmount) === 0 &&
    payLaterAmount &&
    Number(payLaterAmount) === 0
  );
}

function isOfflineCheckoutFlow(createCheckoutResponse: CreateCheckoutResponse) {
  const payNowAmount = createCheckoutResponse?.checkout?.payNow?.total?.amount;
  return payNowAmount && Number(payNowAmount) === 0;
}

async function createOrder(
  createCheckoutResponse: CreateCheckoutResponse,
  httpClient: IHttpClient,
) {
  return (
    await httpClient.request(
      ecomCreateOrder({ id: createCheckoutResponse.checkout?.id! }),
    )
  ).data;
}

function shouldNotGoThroughCheckout({
  couponCheckboxChecked,
  createCheckoutResponse,
  slotServices = {},
}: {
  createCheckoutResponse: CreateCheckoutResponse;
  isCart?: boolean;
  couponCheckboxChecked?: boolean;
  slotServices?: SlotServices;
}) {
  const firstService = mapToArray<SlotService>(slotServices)[0];

  const forceCheckout =
    firstService.service.hasCancellationFee ||
    firstService.service.hasSaveCreditCardPolicy;

  if (forceCheckout) {
    return false;
  }

  return (
    isFreeOrPricePlanCheckoutFlow(createCheckoutResponse) ||
    (!couponCheckboxChecked && isOfflineCheckoutFlow(createCheckoutResponse))
  );
}
